'use client'

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import { ImageIcon, LanguagesIcon, ProjectorIcon, PlusCircleIcon, SaveIcon, XIcon, VideoIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { FileSelector } from '@/components/partials/files'
import { LoadingIndicator, UnsavedChangesGuardDialog } from '@/components/partials'
import { CreateSlideParams, SlideTranslationParam, SlideTranslationsParam, SlideType } from '@/types/slide'
import { FileType } from '@/types/file'
import { useMetaStore } from '@/store'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { FormModeEnum, MediaTypeEnum, MimeTypes } from '@/enums'
import { setValidationErrors } from '@/lib/utils'

type PropsType = {
  initialData?: SlideType | null
  onSubmit: (data: CreateSlideParams) => void
  onSubmitAndNew?: (data: CreateSlideParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type SlideFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  mediaType: MediaTypeEnum
  pictureId: string
  videoId: string
  videoUrl: string
  translations: SlideTranslationsParam
}

const SlideForm = forwardRef<SlideFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    // Wrap the metaState.response?.locales in its own useMemo to prevent unnecessary re-renders
    const localesData = useMemo(() => metaState.response?.locales, [metaState.response])
    const locales = useMemo(() => localesData || [], [localesData])

    // State for picture and video files
    const [picture, setPicture] = useState<FileType | null>(initialData?.picture ?? null)
    const [video, setVideo] = useState<FileType | null>(initialData?.video ?? null)

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const hasLocaleError = (localeCode: string, field: keyof SlideTranslationParam): boolean => {
      const translationErrors = formErrors.translations as Record<string, Record<string, unknown>> | undefined
      return !!translationErrors?.[localeCode]?.[field]
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<string, z.ZodType<SlideTranslationParam>> = {}

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          title: z.string().optional(),
          content: z.string().optional(),
          url: z
            .union([z.literal(''), z.url(t('invalid_url'))])
            .optional()
            .nullable()
            .transform(val => (val === '' ? '' : val || null))
        })
      })

      return z
        .object({
          mediaType: z.enum(MediaTypeEnum),
          pictureId: z.string().optional(),
          videoId: z.string().optional(),
          videoUrl: z
            .union([z.literal(''), z.url(t('invalid_url'))])
            .optional()
            .transform(val => val || ''),
          translations: z.object(translationsSchema)
        })
        .superRefine((data, ctx) => {
          if (data.mediaType === MediaTypeEnum.IMAGE && !data.pictureId) {
            ctx.addIssue({ code: 'custom', path: ['pictureId'], message: t('required') })
          }
          if (data.mediaType === MediaTypeEnum.VIDEO && !data.videoId) {
            ctx.addIssue({ code: 'custom', path: ['videoId'], message: t('required') })
          }
          if (data.mediaType === MediaTypeEnum.YOUTUBE && !data.videoUrl) {
            ctx.addIssue({ code: 'custom', path: ['videoUrl'], message: t('required') })
          }
        })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: SlideTranslationsParam = {}

      locales.forEach(locale => {
        // Ensure values are always strings, never undefined
        const translation = initialData?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          title: translation?.title ?? '',
          content: translation?.content ?? '',
          url: translation?.url ?? ''
        }
      })

      return {
        mediaType: initialData?.mediaType ?? MediaTypeEnum.IMAGE,
        pictureId: initialData?.picture?.id ?? '',
        videoId: initialData?.video?.id ?? '',
        videoUrl: initialData?.videoUrl ?? '',
        translations: defaultTranslations
      }
    }, [locales, initialData])

    const form = useForm<FormData>({
      resolver: zodResolver(schema) as Resolver<FormData>,
      defaultValues: getDefaultValues(),
      mode: 'onChange'
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    // Update form values when initialData or locales change
    useEffect(() => {
      if (locales.length > 0) {
        const newDefaultValues = getDefaultValues()
        form.reset(newDefaultValues)
      }
    }, [getDefaultValues, locales, form])

    const formErrors = form.formState.errors

    const handlePictureChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setPicture(null)
        form.setValue('pictureId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setPicture(files[0])
        form.setValue('pictureId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setPicture(files)
        form.setValue('pictureId', files?.id || '', { shouldValidate: true })
      }
    }

    const handleVideoChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setVideo(null)
        form.setValue('videoId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setVideo(files[0])
        form.setValue('videoId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setVideo(files)
        form.setValue('videoId', files?.id || '', { shouldValidate: true })
      }
    }

    const buildSubmissionData = (data: FormData): CreateSlideParams => {
      const submissionData: CreateSlideParams = {
        type: data.mediaType,
        translations: {}
      }

      if (data.mediaType === MediaTypeEnum.IMAGE) {
        submissionData.pictureId = picture?.id || ''
      } else if (data.mediaType === MediaTypeEnum.VIDEO) {
        submissionData.videoId = video?.id || ''
      } else {
        submissionData.videoUrl = data.videoUrl || ''
      }

      // Add translations
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || '',
          content: data.translations[locale.fullCode]?.content || '',
          url: data.translations[locale.fullCode]?.url || ''
        }
      })

      return submissionData
    }

    const handleSubmit = (data: FormData) => {
      onSubmit(buildSubmissionData(data))
    }

    const handleSubmitAndNew = (data: FormData) => {
      onSubmitAndNew?.(buildSubmissionData(data))
    }

    if (!metaState.response?.locales) {
      return <LoadingIndicator />
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ProjectorIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_slide') : t('edit_slide')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.title"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError =
                          hasLocaleError(locale.fullCode, 'title') ||
                          hasLocaleError(locale.fullCode, 'content') ||
                          hasLocaleError(locale.fullCode, 'url')

                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasTitleError = hasLocaleError(locale.fullCode, 'title')
                      const hasContentError = hasLocaleError(locale.fullCode, 'content')
                      const hasUrlError = hasLocaleError(locale.fullCode, 'url')
                      const hasError = hasTitleError || hasContentError || hasUrlError

                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.title`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('title')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('title')}
                                    className={`${hasTitleError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.content`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('content')}</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder={t('content')}
                                    className={`${hasContentError ? 'border-destructive focus:border-destructive' : ''}`}
                                    value={field.value || ''}
                                    autoFocus={false}
                                    onChange={html => field.onChange(html)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.url`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('url')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('title')}
                                    className={`${hasUrlError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p>{t('no_locales_available')}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Media */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name={'pictureId'}
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <FormField
                  control={form.control}
                  name={'videoId'}
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <VideoIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('media_type')}</h3>
                </div>

                <FormField
                  control={form.control}
                  name="mediaType"
                  render={({ field }) => (
                    <FormItem className="max-w-xs">
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('media_type')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={MediaTypeEnum.IMAGE}>{t('media_type_image')}</SelectItem>
                          <SelectItem value={MediaTypeEnum.VIDEO}>{t('media_type_video')}</SelectItem>
                          <SelectItem value={MediaTypeEnum.YOUTUBE}>{t('media_type_youtube')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch('mediaType') === MediaTypeEnum.IMAGE && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      <h3 className="font-semibold">{t('picture')}</h3>
                      <span className="text-xs">2040x518 px</span>
                    </div>
                    <FileSelector
                      multiple={false}
                      onlyImages={true}
                      optimizeImageSelection={true}
                      value={picture}
                      onChange={handlePictureChange}
                      buttonLabel={t('select_picture')}
                      buttonLabelChange={t('change_picture')}
                      noSelectionText={t('no_file_selected')}
                      allowMainSelection={true}
                      initialMainId={initialData?.picture?.id}
                    />
                    {formErrors.pictureId && (
                      <p className="text-destructive mt-2 text-sm">
                        {String(formErrors.pictureId.message || t('required'))}
                      </p>
                    )}
                  </div>
                )}

                {form.watch('mediaType') === MediaTypeEnum.VIDEO && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <VideoIcon className="h-5 w-5" />
                      <h3 className="font-semibold">{t('video_file')}</h3>
                      <span className="text-xs">MP4</span>
                    </div>
                    <FileSelector
                      multiple={false}
                      onlyImages={false}
                      optimizeImageSelection={false}
                      types={[MimeTypes.MP4]}
                      value={video}
                      onChange={handleVideoChange}
                      buttonLabel={t('select_video')}
                      buttonLabelChange={t('change_video')}
                      noSelectionText={t('no_file_selected')}
                    />
                    {formErrors.videoId && (
                      <p className="text-destructive mt-2 text-sm">
                        {String(formErrors.videoId.message || t('required'))}
                      </p>
                    )}
                  </div>
                )}

                {form.watch('mediaType') === MediaTypeEnum.YOUTUBE && (
                  <FormField
                    control={form.control}
                    name="videoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('video_url')}</FormLabel>
                        <FormControl>
                          <Input placeholder="https://www.youtube.com/embed/..." {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    {isLoading || form.formState.isSubmitting
                      ? t('saving')
                      : isCreateModeWithSubmitAndNew
                        ? t('save_and_add_new')
                        : t(mode)}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

SlideForm.displayName = 'SlideForm'

export default SlideForm
